import { HttpClient } from "@angular/common/http";
import { environment as env } from "../../../../../environments/environment.prod";
import { Injectable } from "@angular/core";
import { AbstractService } from "app/services/abstract.service";
import { Observable } from "rxjs";
import { IAPIListResult, IPagination, IPaginationResult, IRequestResult } from "app/common/interfaces";
import { andWhere, buildQuery, CombinedFilter, FilterFn, noop, Ops, orderBy, OrderVariant, orWhere, page, select, where } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { ICreateAssetControl, IUpdateAssetControl } from "./interfaces/asset-control.interface";
import AssetControlAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/assets/asset-control/asset-control.entity";
import { IFilters } from "../../filters/interfaces/filterInterface";

const apiUrl = env.API_AVE_ASSET;
const moduleName = "asset_control";

@Injectable({
  providedIn: "root",
})
export class AssetControlService extends AbstractService<AssetControlAttributes, ICreateAssetControl, IUpdateAssetControl> {

    constructor(
        http: HttpClient,
    ) {
        super(apiUrl, moduleName, http);
    }

    /**
     * Obtiene activos asignados con paginación y filtros.
     *
     * Estrategia de búsqueda:
     * - Sin búsqueda: Un solo objeto con filtros base
     * - Con búsqueda: Ocho objetos (OR lógico) - busca en 'asset.name', 'asset.code', 'distributionChain.name',
     *   'salesOffice.name', 'shipTo.name', 'status', 'document', 'subRoute.name'
     *
     * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
     *
     * @param options Pagination options
     * @param filter Filters to apply
     * @returns Data list
     *
     * Search fields: asset.name, asset.code, distributionChain.name, salesOffice.name,
     *                shipTo.name, status, document, subRoute.name
     * Array filters: distributionChainId, salesOfficeId, status
     * Single filters: startDate/endDate
     */
    getAllPaginate(
        options: IPagination<AssetControlAttributes>,
        filter: IFilters
    ): Observable<IPaginationResult<AssetControlAttributes>> {

        const hasSearch = !!options.search;

        /**
         * Construye las condiciones base que SIEMPRE deben aplicarse.
         * Incluye: _deleted=false, filtros de distribución, oficina, estado, fechas.
         *
         * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
         */
        const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<AssetControlAttributes>[] => {
            const deletedFilter: FilterFn<AssetControlAttributes> = params.useOrWhere
                ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
                : where('_deleted', Ops.eq(false));   // Primer objeto del array

            const filters: FilterFn<AssetControlAttributes>[] = [deletedFilter];

            if (filter?.distributionChainId && Array.isArray(filter.distributionChainId) && filter.distributionChainId.length > 0) {
                filters.push(andWhere('distributionChain.id' as keyof AssetControlAttributes, Ops.in(...filter.distributionChainId)));
            }

            if (filter?.salesOfficeId && filter.salesOfficeId.length > 0) {
                filters.push(andWhere('salesOffice.id' as keyof AssetControlAttributes, Ops.in(...filter.salesOfficeId)));
            }

            if (filter?.status && filter.status.length > 0) {
                filters.push(andWhere('statusId', Ops.in(...filter.status)));
            }

            if (filter?.startDate && filter?.endDate) {
                filters.push(andWhere("assignmentStartDate" as keyof AssetControlAttributes, Ops.btwnDates(filter.startDate, filter.endDate)));
            }

            return filters;
        };

        // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

        // Objeto 1: Condiciones base + búsqueda por ASSET NAME (o solo condiciones si no hay búsqueda)
        const searchByAssetNameQueryObject: FilterFn<AssetControlAttributes>[] = [
            ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
            hasSearch ? andWhere("asset.name" as keyof AssetControlAttributes, Ops.like(options.search!, "i")) : noop()
        ];

        // Objeto 2: Condiciones base + búsqueda por ASSET CODE (solo si hay búsqueda)
        const searchByAssetCodeQueryObject: FilterFn<AssetControlAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
            andWhere("asset.code" as keyof AssetControlAttributes, Ops.like(options.search!, "i"))
        ] : [];

        // Objeto 3: Condiciones base + búsqueda por DISTRIBUTION CHAIN NAME (solo si hay búsqueda)
        const searchByDistributionChainQueryObject: FilterFn<AssetControlAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere("distributionChain.name" as keyof AssetControlAttributes, Ops.like(options.search!, "i"))
        ] : [];

        // Objeto 4: Condiciones base + búsqueda por SALES OFFICE NAME (solo si hay búsqueda)
        const searchBySalesOfficeQueryObject: FilterFn<AssetControlAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere("salesOffice.name" as keyof AssetControlAttributes, Ops.like(options.search!, "i"))
        ] : [];

        // Objeto 5: Condiciones base + búsqueda por SHIP TO NAME (solo si hay búsqueda)
        const searchByShipToQueryObject: FilterFn<AssetControlAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere("shipTo.name" as keyof AssetControlAttributes, Ops.like(options.search!, "i"))
        ] : [];

        // Objeto 6: Condiciones base + búsqueda por STATUS (solo si hay búsqueda)
        const searchByStatusQueryObject: FilterFn<AssetControlAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere("status", Ops.like(options.search!, "i"))
        ] : [];

        // Objeto 7: Condiciones base + búsqueda por DOCUMENT (solo si hay búsqueda)
        const searchByDocumentQueryObject: FilterFn<AssetControlAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere("document", Ops.like(options.search!, "i"))
        ] : [];

        // Objeto 8: Condiciones base + búsqueda por SUB ROUTE NAME (solo si hay búsqueda)
        const searchBySubRouteQueryObject: FilterFn<AssetControlAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere("subRoute.name" as keyof AssetControlAttributes, Ops.like(options.search!, "i"))
        ] : [];

        // ========== ENSAMBLAJE FINAL ==========

        const query = buildQuery<AssetControlAttributes>(
            // Expandimos los ocho objetos en el buildQuery
            ...searchByAssetNameQueryObject,
            ...searchByAssetCodeQueryObject,
            ...searchByDistributionChainQueryObject,
            ...searchBySalesOfficeQueryObject,
            ...searchByShipToQueryObject,
            ...searchByStatusQueryObject,
            ...searchByDocumentQueryObject,
            ...searchBySubRouteQueryObject,

            // Ordenamiento y paginación
            orderBy(
                options.sort ?? "asset",
                options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
            ),
            page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

        const API_URL: string = apiUrl +
            `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
        return this.http.get<IPaginationResult<AssetControlAttributes>>(API_URL);
    }



}
